/** @typedef {'running' | 'scroll' | 'pdf' | 'document'} IViewMode */

/** @typedef {'multi_frame_single_file' | 'multi_file_single_frame' | null} IDicomStructure */

/**
 * @typedef {Object} IModalityConfig
 * @property {string} key
 * @property {string} label
 * @property {string[]} accepted_ext
 * @property {number} size_max_mb
 * @property {IViewMode} view_mode
 * @property {boolean} export_video
 * @property {IDicomStructure} [dicom_structure]
 */

/** Fallback when /api/ref/modalities is unavailable — mirrors backend study-types.js */
export const DEFAULT_MODALITIES = [
  {
    key: "pre_echo",
    label: "Pre-Echo",
    view_mode: "running",
    export_video: true,
    accepted_ext: [".dcm", ".avi", ".doc", ".docx", ".pdf", ".zip"],
    size_max_mb: 400,
    dicom_structure: "multi_frame_single_file",
  },
  {
    key: "post_echo",
    label: "Post-Echo",
    view_mode: "running",
    export_video: true,
    accepted_ext: [".dcm", ".avi", ".doc", ".docx", ".pdf", ".zip"],
    size_max_mb: 400,
    dicom_structure: "multi_frame_single_file",
  },
  {
    key: "ct_images",
    label: "Raw CT Image",
    view_mode: "scroll",
    export_video: false,
    accepted_ext: [".zip"],
    size_max_mb: 2048,
    dicom_structure: "multi_file_single_frame",
  },
  {
    key: "mri",
    label: "MRI",
    view_mode: "scroll",
    export_video: false,
    accepted_ext: [".zip"],
    size_max_mb: 4096,
    dicom_structure: "multi_file_single_frame",
  },
  {
    key: "ct_report",
    label: "CT Report",
    view_mode: "pdf",
    export_video: false,
    accepted_ext: [".pdf"],
    size_max_mb: 200,
    dicom_structure: null,
  },
  {
    key: "3mensio_ct",
    label: "3Mensio Working CT",
    view_mode: "scroll",
    export_video: false,
    accepted_ext: [".zip"],
    size_max_mb: 2048,
    dicom_structure: "multi_file_single_frame",
  },
  {
    key: "fluoro",
    label: "Case Image Fluoro",
    view_mode: "running",
    export_video: true,
    accepted_ext: [".dcm", ".zip"],
    size_max_mb: 2048,
    dicom_structure: "multi_frame_single_file",
  },
  {
    key: "other",
    label: "Other",
    view_mode: "document",
    export_video: false,
    accepted_ext: [".doc", ".docx", ".pdf", ".ppt", ".pptx"],
    size_max_mb: 50,
    dicom_structure: null,
  },
];

export const MODALITY_BADGE_CLASSES = {
  pre_echo: "badge-echo",
  post_echo: "badge-echo",
  ct_images: "badge-ct",
  mri: "badge-mri",
  ct_report: "badge-xray",
  "3mensio_ct": "badge-ct",
  fluoro: "badge-fluoro",
  other: "badge-mri",
};

/** Frontend-only display labels (API/backend keys stay unchanged). */
export const MODALITY_LABEL_OVERRIDES = {
  ct_images: "Raw CT Image",
};

export const getModalityDisplayLabel = (key, fallbackLabel) => {
  if (key && MODALITY_LABEL_OVERRIDES[key]) return MODALITY_LABEL_OVERRIDES[key];
  if (fallbackLabel) return fallbackLabel;
  if (!key) return "";
  return String(key).replace(/_/g, " ");
};

export const getModalityBadgeClass = (key) => MODALITY_BADGE_CLASSES[key] ?? "badge-ct";

export const MODALITY_CHART_COLORS = {
  ct_report: "#F05A28",
  pre_echo: "#1B6B43",
  post_echo: "#2A9D8F",
  "3mensio_ct": "#1E4A8C",
  ct_images: "#8AB4F8",
  fluoro: "#7C5CB8",
  mri: "#C4A35A",
  other: "#C8C8C8",
};

const FALLBACK_CHART_COLORS = [
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#8b5cf6",
  "#6366f1",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
];

const hashModalityKey = (key) => {
  const text = String(key);
  let hash = 0;
  for (let i = 0; i < text.length; i += 1) {
    hash = ((hash << 5) - hash) + text.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

/** Stable chart color for a modality key — never depends on sort order or index. */
export const getModalityChartColor = (key) => {
  if (key && MODALITY_CHART_COLORS[key]) return MODALITY_CHART_COLORS[key];
  if (!key) return FALLBACK_CHART_COLORS[0];
  return FALLBACK_CHART_COLORS[hashModalityKey(key) % FALLBACK_CHART_COLORS.length];
};

const OTHER_PPT_EXTENSIONS = [".ppt", ".pptx"];

export const isOtherModality = (key) => key === "other";

/** PowerPoint files have no DICOM/patient tags — Add Study should skip mismatch review. */
export const isPowerpointFile = (filename) => {
  const ext = getFileExtension(filename);
  return OTHER_PPT_EXTENSIONS.includes(ext);
};

/** Ensure Other always accepts PPT, even if /api/ref/modalities is stale. */
export const withOtherPptExtensions = (key, acceptedExt) => {
  const list = Array.isArray(acceptedExt) ? [...acceptedExt] : [];
  if (key !== "other") return list;
  OTHER_PPT_EXTENSIONS.forEach((ext) => {
    if (!list.some((item) => String(item).toLowerCase() === ext)) list.push(ext);
  });
  return list;
};

export const getFileExtension = (filename) => {
  const name = filename?.toLowerCase() ?? "";
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot) : "";
};

export const isExtensionAccepted = (filename, acceptedExt) => {
  const ext = getFileExtension(filename);
  return acceptedExt?.some((a) => a.toLowerCase() === ext) ?? false;
};

export const buildAcceptAttribute = (acceptedExt) =>
  acceptedExt?.join(",") ?? "";

export const formatAcceptedExtensions = (acceptedExt) =>
  acceptedExt?.map((e) => e.toUpperCase()).join(", ") ?? "";

const ZIP_ONLY_KEYS = new Set(["ct_images", "mri", "3mensio_ct"]);

/**
 * @param {IModalityConfig | undefined} config
 * @param {string} filename
 */
export const getValidationError = (config, filename) => {
  if (!config) return null;
  if (isExtensionAccepted(filename, config.accepted_ext)) return null;

  const accepted = formatAcceptedExtensions(config.accepted_ext);
  if (ZIP_ONLY_KEYS.has(config.key)) {
    return `${config.label} requires a ZIP archive of DICOM slices`;
  }
  return `${config.label} only accepts: ${accepted}`;
};

/**
 * @param {IModalityConfig | undefined} config
 * @param {number} sizeBytes
 */
export const getSizeValidationError = (config, sizeBytes) => {
  if (!config?.size_max_mb) return null;
  const maxBytes = config.size_max_mb * 1024 * 1024;
  if (sizeBytes <= maxBytes) return null;
  return `${config.label} exceeds the ${config.size_max_mb} MB limit`;
};

export const OFFICE_DOCUMENT_EXTENSIONS = [".doc", ".docx", ".ppt", ".pptx"];

export const isPdfFile = (filename) => getFileExtension(filename) === ".pdf";

export const isOfficeDocumentFile = (filename) =>
  OFFICE_DOCUMENT_EXTENSIONS.includes(getFileExtension(filename));

export const getMicrosoftOfficeEmbedUrl = (fileUrl) =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileUrl)}`;

export const inferFileType = (filename) => {
  const ext = getFileExtension(filename).replace(".", "");
  if (["dcm", "dicom"].includes(ext)) return "dicom";
  if (ext === "pdf") return "pdf";
  if (["mp4", "avi", "mov", "webm"].includes(ext)) return "video";
  if (ext === "zip") return "zip";
  if (["doc", "docx", "ppt", "pptx"].includes(ext)) return "document";
  return "image";
};

export const acceptsOnlyZip = (config) =>
  config?.accepted_ext?.length === 1 && config.accepted_ext[0]?.toLowerCase() === ".zip";
